/**
 * One write path for crop and rotation on `students/{id}.artwork.{artworkId}`.
 *
 * A URL is not enough to save: the update is leaf field paths on that
 * artwork id, and a missing id must not be resurrected as a skeleton entry.
 * Display surfaces subscribe to the student and prefer `cropUrl`, so the
 * same crop shows everywhere. The original `url` is never replaced.
 */
import * as Sentry from "@sentry/react"
import { deleteField } from "firebase/firestore"
import { isEmpty } from "lodash"

import AmplitudeAnalytics from "#classes/AmplitudeAnalytics"
import Firebase from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { type ArtworkCropResult } from "#components/ArtworkTiles/ArtworkCropModal"
import { buildArtworkRotationUpdate } from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { type GenericObject, invariant } from "#customTypes/types"
import {
  buildArtworkCropUpdate,
  getArtworkCropRevertPaths,
  type SavedCropFields,
} from "#utils/artworkCropHelpers"
import { uploadArtworkCrop } from "#utils/artworkCropUpload"
import { parseStoragePathFromUrl } from "#utils/artworkRenditionHelpers"

export type ArtworkEditAction = "zoom" | "rotate" | "crop" | "revert"

export const trackArtworkEdit = (parameters: {
  source: string
  action: ArtworkEditAction
  studentId: string
  artworkId: string
}) => {
  AmplitudeAnalytics.trackEvent(
    ANALYTICS_EVENTS.ARTWORK_IMAGE_EDITED,
    parameters
  )
}

/**
 * Fields a save has written but the student subscription has not echoed yet.
 * `null` clears a crop field (revert). `undefined` leaves it alone.
 */
export interface ArtworkEditOverride {
  cropUrl?: string | null
  cropPath?: string | null
  thumbnailUrl?: string | null
  thumbnailPath?: string | null
  rotation?: number
}

const without = <K extends keyof StudentArtwork>(
  artwork: StudentArtwork,
  key: K
): StudentArtwork => {
  const next = { ...artwork }
  delete next[key]
  return next
}

/** Paint a just-saved crop or rotation until the student listener echoes it. */
export const mergeArtworkEditOverride = (
  artwork: StudentArtwork,
  override: ArtworkEditOverride | null
): StudentArtwork => {
  if (!override) return artwork
  let next = artwork
  if (override.cropUrl === null) {
    next = without(next, "cropUrl")
    next = without(next, "cropPath")
  } else if (override.cropUrl) {
    next = {
      ...next,
      cropUrl: override.cropUrl,
      cropPath: override.cropPath ?? next.cropPath,
    }
  }
  if (override.thumbnailUrl === null) {
    next = without(next, "thumbnailUrl")
    next = without(next, "thumbnailPath")
  } else if (override.thumbnailUrl) {
    next = {
      ...next,
      thumbnailUrl: override.thumbnailUrl,
      thumbnailPath: override.thumbnailPath ?? next.thumbnailPath,
    }
  }
  if (override.rotation !== undefined) {
    next = { ...next, rotation: override.rotation }
  }
  return next
}

/** Returns false when none of the ids are on the student, so nothing was written. */
export const saveStudentArtworkRotation = async (
  studentId: Uuid,
  artworkMap: Record<string, StudentArtwork> | undefined,
  rotationTargets: Record<string, number>
): Promise<boolean> => {
  const update = buildArtworkRotationUpdate(artworkMap, rotationTargets)
  if (!update) return false
  try {
    await Firebase.getInstance().updateDocumentOrThrow(
      DOC_TYPES.STUDENTS,
      studentId,
      update
    )
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
  return true
}

/**
 * Uploads a crop beside the original when the editor produced blobs, then
 * writes the crop fields and the display rotation in one update. Rotation
 * stays a CSS angle for both the original and the crop.
 */
export const saveStudentArtworkCrop = async ({
  studentId,
  artworkMap,
  artwork,
  result,
  userId,
}: {
  studentId: Uuid
  artworkMap: Record<string, StudentArtwork> | undefined
  artwork: Pick<StudentArtwork, "id" | "url">
  result: ArtworkCropResult
  /** Audit `currentUserId`, stamped on the piece as `croppedBy`. */
  userId: Uuid
}): Promise<SavedCropFields | null> => {
  try {
    let crop: SavedCropFields | null = null
    if (result.blobs) {
      const originalPath = parseStoragePathFromUrl(artwork.url)
      invariant(
        originalPath != null,
        `Artwork ${artwork.id} has no parsable storage path!`
      )
      crop = await uploadArtworkCrop(result.blobs, originalPath)
    }
    const update = {
      ...(crop
        ? buildArtworkCropUpdate(artworkMap, artwork.id, crop, userId)
        : {}),
      ...buildArtworkRotationUpdate(artworkMap, {
        [artwork.id]: result.rotation,
      }),
    }
    if (isEmpty(update)) {
      throw new Error(`Artwork ${artwork.id} is not on this student`)
    }
    await Firebase.getInstance().updateDocumentOrThrow(
      DOC_TYPES.STUDENTS,
      studentId,
      update as GenericObject
    )
    return crop
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
}

/** Clears crop fields. Storage objects stay; the names are timestamped. */
export const revertStudentArtworkCrop = async (
  studentId: Uuid,
  artwork: StudentArtwork
): Promise<string[] | null> => {
  const fieldPaths = getArtworkCropRevertPaths(
    { [artwork.id]: artwork },
    artwork.id
  )
  if (!fieldPaths) return null
  const update: GenericObject = Object.fromEntries(
    fieldPaths.map((path) => [path, deleteField()])
  )
  try {
    await Firebase.getInstance().updateDocumentOrThrow(
      DOC_TYPES.STUDENTS,
      studentId,
      update
    )
  } catch (error) {
    Sentry.captureException(error)
    throw error
  }
  return fieldPaths
}
